import { useCallback, useEffect, useRef, useState } from "react";

import { listActionsByNurseryQueryControllerListByNursery } from "@/generated/follow-up-actions/follow-up-actions";
import type { FollowUpActionResponseDto } from "@/generated/model";
import { offlineDb } from "@/lib/offline/db";
import { syncService } from "@/lib/offline/syncService";

// Extended item type that includes sync status
export interface FollowUpActionWithSync extends FollowUpActionResponseDto {
  localSyncStatus?: {
    isLocallyCompleted: boolean;
    pendingCount: number;
    failedCount: number;
  };
}

interface UseOfflineFollowUpActionsResult {
  items: FollowUpActionWithSync[];
  isLoading: boolean;
  isOffline: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
}

const CACHE_KEY_PREFIX = "follow-up-actions-";

/**
 * Offline-first hook for fetching follow-up actions.
 *
 * Flow:
 * 1. Immediately return cached data from IndexedDB (if available)
 * 2. If online, fetch fresh data from server in background
 * 3. Merge with local completion state (for unsynced items)
 * 4. Update cache and UI with merged data
 *
 * Status Priority:
 * - Server status is authoritative (if server says COMPLETED, trust it)
 * - Local completion state only adds "syncing" indicator for unsynced items
 */
export function useOfflineFollowUpActions(
  nurseryId: string | null,
  options?: { checklistView?: boolean },
): UseOfflineFollowUpActionsResult {
  const [items, setItems] = useState<FollowUpActionWithSync[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isOffline, setIsOffline] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const initializedRef = useRef(false);
  const currentNurseryIdRef = useRef(nurseryId);

  const cacheKey = nurseryId ? `${CACHE_KEY_PREFIX}${nurseryId}` : null;

  // Merge server/cached items with local completion state
  const mergeWithLocalState = useCallback(
    async (
      serverItems: FollowUpActionResponseDto[],
    ): Promise<FollowUpActionWithSync[]> => {
      if (serverItems.length === 0) return [];

      const actionIds = serverItems.map((item) => item.id);

      // Get local completions for these actions
      const localCompletions = await offlineDb.followUpCompletions
        .filter((c) => actionIds.includes(c.id))
        .toArray();

      const localCompletionMap = new Map(
        localCompletions.map((c) => [c.id, c]),
      );

      // Get sync queue items for follow-up actions
      const queueItems = await offlineDb.syncQueue
        .filter(
          (i) => i.type === "followUpPhoto" || i.type === "followUpFinalize",
        )
        .toArray();

      // Map queue items to action IDs
      const syncStatusMap = new Map<
        string,
        { pending: number; failed: number }
      >();

      for (const item of queueItems) {
        let actionId: string | undefined;

        if (item.type === "followUpFinalize") {
          actionId = item.entityId;
        } else if (item.type === "followUpPhoto") {
          const photo = await offlineDb.followUpPhotos.get(item.entityId);
          if (photo) {
            actionId = photo.actionId;
          }
        }

        if (actionId && actionIds.includes(actionId)) {
          const current = syncStatusMap.get(actionId) || {
            pending: 0,
            failed: 0,
          };
          if (item.attempts >= 5) {
            current.failed++;
          } else {
            current.pending++;
          }
          syncStatusMap.set(actionId, current);
        }
      }

      return serverItems.map((item) => {
        const localCompletion = localCompletionMap.get(item.id);
        const syncStatus = syncStatusMap.get(item.id);

        const isLocallyCompleted =
          item.status !== "COMPLETED" &&
          item.status !== "CANCELLED" &&
          localCompletion?.completedAt != null &&
          localCompletion.syncState !== "synced";

        if (
          isLocallyCompleted ||
          (syncStatus && (syncStatus.pending > 0 || syncStatus.failed > 0))
        ) {
          return {
            ...item,
            localSyncStatus: {
              isLocallyCompleted,
              pendingCount: syncStatus?.pending || 0,
              failedCount: syncStatus?.failed || 0,
            },
          };
        }

        return item;
      });
    },
    [],
  );

  // Load cached data from IndexedDB
  const loadFromCache = useCallback(async (): Promise<
    FollowUpActionResponseDto[] | null
  > => {
    if (!cacheKey) return null;

    try {
      const cached = await offlineDb.followUpActions.get(cacheKey);
      if (cached) {
        return cached.items as unknown as FollowUpActionResponseDto[];
      }
    } catch (err) {
      console.error("[OfflineFollowUpActions] Failed to load from cache:", err);
    }
    return null;
  }, [cacheKey]);

  // Save data to IndexedDB cache
  const saveToCache = useCallback(
    async (data: FollowUpActionResponseDto[]) => {
      if (!cacheKey || !nurseryId) return;

      try {
        await offlineDb.followUpActions.put({
          id: cacheKey,
          nurseryId,
          items: data as unknown as FollowUpActionResponseDto[],
          cachedAt: Date.now(),
        });
      } catch (err) {
        console.error("[OfflineFollowUpActions] Failed to save to cache:", err);
      }
    },
    [cacheKey, nurseryId],
  );

  // Fetch fresh data from server
  const fetchFromServer = useCallback(async (): Promise<
    FollowUpActionResponseDto[] | null
  > => {
    if (!nurseryId) return null;

    const params = options?.checklistView ? { checklistView: true } : undefined;

    const response = await listActionsByNurseryQueryControllerListByNursery(
      nurseryId,
      params,
    );
    return (response?.data as FollowUpActionResponseDto[]) || [];
  }, [nurseryId, options?.checklistView]);

  // Main refetch function
  const refetch = useCallback(async () => {
    if (!nurseryId || !navigator.onLine) return;

    try {
      const freshData = await fetchFromServer();
      if (freshData) {
        const mergedData = await mergeWithLocalState(freshData);
        setItems(mergedData);
        await saveToCache(freshData);
      }
      setError(null);
    } catch (err) {
      setError(
        err instanceof Error ? err : new Error("Failed to fetch actions"),
      );
    }
  }, [nurseryId, fetchFromServer, saveToCache, mergeWithLocalState]);

  // Refresh local sync status without server fetch
  const refreshLocalState = useCallback(async () => {
    if (items.length === 0) return;

    // Strip localSyncStatus to get base items
    const baseItems = items.map((item) => {
      const { localSyncStatus: _, ...baseItem } = item;
      return baseItem as FollowUpActionResponseDto;
    });

    const mergedData = await mergeWithLocalState(baseItems);
    setItems(mergedData);
  }, [items, mergeWithLocalState]);

  // Initial load effect
  useEffect(() => {
    // Reset if nurseryId changes
    if (currentNurseryIdRef.current !== nurseryId) {
      currentNurseryIdRef.current = nurseryId;
      initializedRef.current = false;
    }

    if (initializedRef.current) return;

    let isMounted = true;

    async function initialize() {
      if (!nurseryId) return;

      const offline = !navigator.onLine;
      setIsOffline(offline);

      // Step 1: Load cached data immediately
      const cachedItems = await loadFromCache();

      if (!isMounted) return;

      if (cachedItems && cachedItems.length > 0) {
        const mergedData = await mergeWithLocalState(cachedItems);
        if (isMounted) {
          setItems(mergedData);
          setIsLoading(false);
        }
      }

      // Step 2: If online, fetch fresh data in background
      if (!offline) {
        try {
          const freshData = await fetchFromServer();
          if (freshData && isMounted) {
            const mergedData = await mergeWithLocalState(freshData);
            setItems(mergedData);
            await saveToCache(freshData);
          }
          if (isMounted) setError(null);
        } catch (err) {
          if (isMounted) {
            if (!cachedItems || cachedItems.length === 0) {
              setError(
                err instanceof Error
                  ? err
                  : new Error("Failed to fetch actions"),
              );
            }
          }
        }
      }

      if (isMounted) {
        setIsLoading(false);
        initializedRef.current = true;
      }
    }

    initialize();

    return () => {
      isMounted = false;
    };
  }, [
    nurseryId,
    loadFromCache,
    fetchFromServer,
    saveToCache,
    mergeWithLocalState,
  ]);

  // Online/offline listeners
  useEffect(() => {
    const handleOnline = () => {
      setIsOffline(false);
      refetch();
    };

    const handleOffline = () => {
      setIsOffline(true);
    };

    setIsOffline(!navigator.onLine);

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, [refetch]);

  // Refs to hold latest callbacks - prevents subscription from being recreated
  const refetchRef = useRef(refetch);
  const refreshLocalStateRef = useRef(refreshLocalState);

  useEffect(() => {
    refetchRef.current = refetch;
  }, [refetch]);

  useEffect(() => {
    refreshLocalStateRef.current = refreshLocalState;
  }, [refreshLocalState]);

  const previousPendingCountRef = useRef(0);

  // Subscribe to sync service for live updates
  useEffect(() => {
    const unsubscribe = syncService.subscribe((status) => {
      const previousPendingCount = previousPendingCountRef.current;

      if (previousPendingCount > 0 && status.pendingCount === 0) {
        console.log(
          "[OfflineFollowUpActions] Sync complete - refetching from server",
        );
        refetchRef.current();
      } else if (status.pendingCount !== previousPendingCount) {
        console.log(
          "[OfflineFollowUpActions] Sync status changed - refreshing local state",
        );
        refreshLocalStateRef.current();
      }

      previousPendingCountRef.current = status.pendingCount;
    });

    return () => {
      unsubscribe();
    };
  }, []);

  return {
    items,
    isLoading,
    isOffline,
    error,
    refetch,
  };
}
